import { useQuery } from "@apollo/client";
import RecordingJobTypeSelection from "@components/common/record/RecordingJobTypeSelection.js";
import { isUUID } from "@components/dashboard/utils.js";
import { getSelectedTeamMemberName } from "@components/teams/utils.js";
import {
  faChevronDown,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_TEAM_USER_NOTE_COUNTS from "@graphql/queries/GetTeamUserNoteCounts.js";
import {
  useAuth,
  useLocalStorage,
  useWatchUserWsEvent,
} from "@hooks";
import { endOfDay, startOfDay } from "date-fns";
import moment from "moment";
import { useState } from "react";

import DateRangeSelection from "./DateRangeSelection.js";
import { formatDateTime } from "./utils.js";
import TeamFilterDropdown from "../teams/TeamFilterDropdown.js";

export function FilterOption({
  label,
  children,
  isActive,
  setActiveFilter,
  containerName,
  width = "w-auto",
  isApplied,
  onClear,
  value,
  dataTestId,
}) {
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  return (
    <div className="relative">
      <div
        className={`rounded-full border h-6 px-2 text-sm cursor-pointer transition-colors flex items-center space-x-1
            ${
              isApplied
                ? "border-indigo-500 bg-indigo-500 text-white hover:bg-indigo-600"
                : "border-indigo-500 text-indigo-500 hover:bg-indigo-200 dark:hover:bg-gray-800"
            }`}
        data-testid={dataTestId}
        onMouseEnter={() => {
          setActiveFilter(label);
        }}
        onMouseLeave={() => {
          setActiveFilter(null);
        }}
      >
        <span className="whitespace-nowrap">
          {isApplied ? truncateText(value) : label}
        </span>
        {isApplied ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="hover:bg-indigo-600 rounded-full p-0.5"
            data-testid="clear-filter-button"
          >
            <FontAwesomeIcon icon={faXmark} size="xs" />
          </button>
        ) : (
          <FontAwesomeIcon icon={faChevronDown} size="xs" />
        )}

        <div
          className={`absolute transform translate-y-full bottom-0 left-0 z-40 flex flex-col items-start justify-start rounded-xl shadow-md bg-white dark:bg-gray-800 dark:border-gray-900/40 border border-gray-100 p-2 ${width} ${
            isActive ? "block" : "hidden"
          }`}
          id={containerName}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default function NoteFilters({
  setQueryOptions,
  currentTeam,
  selectedTeamMembers,
  setSelectedTeamMembers,
  selectedJobType,
  setSelectedJobType,
  refreshCurrentTeam,
  noteFilter = "",
}) {
  const { userUuid } = useAuth();
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedRange, setSelectedRange] = useLocalStorage(
    "selected-date-range",
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  );
  const hasDateFilter =
    selectedRange.startDate && selectedRange.endDate;
  const hasTeamFilter =
    (userUuid !== selectedTeamMembers[0] ||
      selectedTeamMembers.length > 1) &&
    selectedTeamMembers.length > 0;
  const hasJobTypeFilter = selectedJobType !== null;

  const noteCountQueryOptions = {
    filter: noteFilter,
    teamUuid: currentTeam?.uuid,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    jobType: selectedJobType?.value,
  };
  const { data, refetch: refetchTeamUserNoteCounts } = useQuery(
    GET_TEAM_USER_NOTE_COUNTS,
    {
      variables: noteCountQueryOptions,
      skip: !currentTeam?.uuid,
      notifyOnNetworkStatusChange: true,
    },
  );

  const checkTeamAndRefetchNoteCounts = () => {
    if (currentTeam?.uuid) {
      refetchTeamUserNoteCounts();
    }
  };
  useWatchUserWsEvent({
    onMessage: checkTeamAndRefetchNoteCounts,
    onConnect: checkTeamAndRefetchNoteCounts,
    eventName: "note_update",
  });

  const userNoteCounts = (
    data?.teamUserNoteCounts?.noteCounts || []
  ).reduce(
    (acc, { userUuid, noteCount }) => ({
      ...acc,
      [userUuid]: noteCount,
    }),
    {},
  );

  const clearDateFilter = () => {
    setSelectedRange({
      startDate: null,
      endDate: null,
      key: "selection",
    });
    setQueryOptions((prev) => ({
      ...prev,
      startDate: null,
      endDate: null,
    }));
  };

  const clearTeamFilter = () => {
    if (userUuid) {
      setSelectedTeamMembers([userUuid]);
      setQueryOptions((prev) => ({
        ...prev,
        userUuids: [userUuid],
      }));
    }
  };

  const clearJobTypeFilter = () => {
    setSelectedJobType(null);
    setQueryOptions((prev) => ({
      ...prev,
      jobType: null,
    }));
  };

  const handleJobTypeChange = (newJobType) => {
    setSelectedJobType(newJobType);
    setQueryOptions((prev) => ({
      ...prev,
      jobType: newJobType?.value,
    }));
  };

  const handleApplyDateRange = (startDate, endDate) => {
    const formattedStartDate = startDate
      ? formatDateTime(startOfDay(startDate))
      : null;
    const formattedEndDate = endDate
      ? formatDateTime(endOfDay(endDate))
      : null;
    setSelectedRange({
      key: "selection",
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });
    setQueryOptions((prevOptions) => ({
      ...prevOptions,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    }));
  };

  const getDateFilterValue = () => {
    if (!selectedRange.startDate || !selectedRange.endDate) return "";

    // If start and end dates are the same, show only one date
    if (
      moment(selectedRange.startDate).format("DD/MM/YY") ===
      moment(selectedRange.endDate).format("DD/MM/YY")
    ) {
      return moment(selectedRange.startDate).format("DD/MM/YY");
    }

    // Otherwise show the range
    return `${moment(selectedRange.startDate).format(
      "DD/MM/YY",
    )} - ${moment(selectedRange.endDate).format("DD/MM/YY")}`;
  };

  const formatJobType = (jobType) => {
    if (!jobType?.value) {
      return "";
    }
    if (isUUID(jobType.value)) {
      return jobType.name;
    }

    // Split on underscores and hyphens
    return jobType.value
      .split(/[-_]/)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(" ");
  };

  return (
    <div
      id="filter-options"
      className="flex flex-row items-center space-x-1 justify-start"
      onMouseLeave={() => {
        setActiveFilter(null);
      }}
    >
      <FilterOption
        label="Date"
        isActive={activeFilter === "Date"}
        setActiveFilter={setActiveFilter}
        containerName="date-filter"
        width="w-[320px]"
        isApplied={hasDateFilter}
        onClear={clearDateFilter}
        value={getDateFilterValue()}
        dataTestId="date-filter-dropdown"
      >
        <div className="w-full">
          <DateRangeSelection
            selectedRange={selectedRange}
            handleApplyDateRange={handleApplyDateRange}
            isActive={activeFilter === "Date"}
            containerName="date-filter"
          />
        </div>
      </FilterOption>

      {currentTeam?.uuid && (
        <FilterOption
          label="My Notes"
          isActive={activeFilter === "My Notes"}
          setActiveFilter={setActiveFilter}
          isApplied={hasTeamFilter}
          onClear={clearTeamFilter}
          value={getSelectedTeamMemberName(
            selectedTeamMembers,
            currentTeam,
            userUuid,
          )}
          dataTestId="team-filter-dropdown"
        >
          <TeamFilterDropdown
            isActive={activeFilter === "My Notes"}
            userNoteCounts={userNoteCounts}
            currentTeam={currentTeam}
            selectedTeamMembers={selectedTeamMembers}
            setSelectedTeamMembers={setSelectedTeamMembers}
            refreshCurrentTeam={refreshCurrentTeam}
          />
        </FilterOption>
      )}
      <FilterOption
        dataTestId="filter-template-dropdown"
        label="Template"
        isActive={activeFilter === "Template"}
        setActiveFilter={setActiveFilter}
        containerName="type-filter"
        width="w-[210px]"
        isApplied={hasJobTypeFilter}
        onClear={clearJobTypeFilter}
        value={formatJobType(selectedJobType)}
      >
        <RecordingJobTypeSelection
          selectedJobType={selectedJobType}
          setSelectedJobType={handleJobTypeChange}
          defaultJobType={{ value: "", name: "All" }}
          isActive={activeFilter === "Template"}
        />
      </FilterOption>
    </div>
  );
}
